// What a PR looks like with a write applied before GitHub confirms it: the
// pending writes' `apply` functions (see pending.svelte.ts). Each takes the
// state without the write and returns it with the write; none mutates.
import type { Actor, PullRequestData, ReviewThread, ThreadComment, TimelineItem } from '../api/queries';
import type { DraftState, ReviewDraft, ServerOverride } from '../api/server';
import type { AttentionMember } from '../engine/types';
import { applyToMembers, applyToOverrides, type AttentionChange } from '../logic/attention';
import { mirroredComment } from '../logic/inbox';
import { dedupKey } from './pending.svelte';

/** One PR as the server sent it (or, with writes applied, as it is shown). */
export interface PrState {
  pr: PullRequestData | null;
  threads: ReviewThread[];
  timeline: TimelineItem[];
  /** The viewer's review drafts, held by the changelist server until published. */
  drafts: ReviewDraft[];
  /** Attention overrides (changelist server). */
  overrides: ServerOverride[];
  /** The attention set computed from the rest. */
  attention: AttentionMember[];
}

/**
 * An item on screen a pending write shows on (see `Writes.on`). Made only
 * by `subject`, so every kind of item has one spelling.
 */
export type Subject = string & { readonly __subject: 'Subject' };

export const subject = {
  /** A review thread (resolving it, or the new thread being saved). */
  thread: (id: string) => `thread:${id}` as Subject,
  /** A comment: in a review thread, or in the conversation. */
  comment: (id: string) => `comment:${id}` as Subject,
  /** A review draft (its row in the Review view, or its note in the code). */
  draft: (id: ReviewDraft['id']) => `draft:${id}` as Subject,
  /** The publish button. */
  publish: 'publish' as Subject,
  /** The merge card. */
  merge: 'merge' as Subject,
  /** The attention set. */
  attention: 'attention' as Subject,
  /** The reviewers card's request form. */
  reviewRequest: 'review-request' as Subject,
} as const;

/**
 * What makes two writes on a PR the same write (see `Writes`): one key per
 * thing the user can send at most once at a time.
 */
export const dedup = {
  /** The text of box `key` (a comment, reply, new thread or held comment being written). */
  draft: (key: string) => dedupKey('draft', key),
  /** Done or Ack on a thread: either one, once. */
  quick: (threadId: string) => dedupKey('quick', threadId),
  /** Resolving or unresolving a thread. */
  thread: (threadId: string) => dedupKey('thread', threadId),
  /** Deleting a draft comment. */
  comment: (commentId: string) => dedupKey('comment', commentId),
  /** Editing or discarding a review draft. */
  draftItem: (id: ReviewDraft['id']) => dedupKey('draft-item', id),
  /** Including a review draft in the review being prepared, or taking it out: the latest choice counts. */
  draftState: (id: ReviewDraft['id']) => dedupKey('draft-state', id),
  /** Publishing (the review and the conversation drafts, or the conversation drafts alone). */
  publish: dedupKey('publish'),
  merge: dedupKey('merge'),
  /** Discarding every draft. */
  discardDrafts: dedupKey('discard', 'drafts'),
  /** Reading the viewer's pending review on GitHub into the drafts that mirror it. */
  mirrorDrafts: dedupKey('mirror', 'drafts'),
  reviewRequest: (login: string) => dedupKey('review-request', login.toLowerCase()),
  /** An attention change for one login (lowercased): the latest change for a login is the one that counts. */
  attention: (login: string) => dedupKey('attention', login.toLowerCase()),
} as const;

export const EMPTY_PR_STATE: PrState = { pr: null, threads: [], timeline: [], drafts: [], overrides: [], attention: [] };

export type ReviewEvent = 'COMMENT' | 'APPROVE' | 'REQUEST_CHANGES';

/** The state GitHub gives a review submitted with `event`. */
const REVIEW_STATE: Record<ReviewEvent, string> = { COMMENT: 'COMMENTED', APPROVE: 'APPROVED', REQUEST_CHANGES: 'CHANGES_REQUESTED' };

/** A comment of the viewer's pending review, not yet saved. */
export function draftComment(c: { id: string; author: Actor | null; body: string; at: string; commit: string | null }): ThreadComment {
  return {
    id: c.id,
    databaseId: 0,
    body: c.body,
    bodyHTML: '',
    createdAt: c.at,
    url: '',
    author: c.author,
    pullRequestReview: { id: '', state: 'PENDING' },
    originalCommit: c.commit ? { oid: c.commit } : null,
  };
}

/** A new review thread holding one draft comment. */
export function draftThread(
  t: { id: string; path: string; side: 'LEFT' | 'RIGHT'; line: number | null; startLine: number | null; subjectType: 'LINE' | 'FILE' },
  first: ThreadComment,
): ReviewThread {
  const file = t.subjectType === 'FILE';
  return {
    id: t.id,
    isResolved: false,
    isOutdated: false,
    path: t.path,
    line: file ? null : t.line,
    originalLine: file ? null : t.line,
    startLine: file ? null : t.startLine,
    originalStartLine: file ? null : t.startLine,
    diffSide: t.side,
    startDiffSide: !file && t.startLine !== null ? t.side : null,
    subjectType: t.subjectType,
    viewerCanResolve: false,
    viewerCanUnresolve: false,
    viewerCanReply: false,
    resolvedBy: null,
    comments: { totalCount: 1, nodes: [first] },
  };
}

const mapThread = (s: PrState, id: string, f: (t: ReviewThread) => ReviewThread): PrState => ({
  ...s,
  threads: s.threads.map((t) => (t.id === id ? f(t) : t)),
});

export const withThread =
  (thread: ReviewThread) =>
  (s: PrState): PrState => ({ ...s, threads: [...s.threads, thread] });

export const withReply =
  (threadId: string, c: ThreadComment) =>
  (s: PrState): PrState =>
    mapThread(s, threadId, (t) => ({ ...t, comments: { ...t.comments, totalCount: t.comments.totalCount + 1, nodes: [...t.comments.nodes, c] } }));

export const withResolved =
  (threadId: string, resolved: boolean, by: string) =>
  (s: PrState): PrState =>
    mapThread(s, threadId, (t) => ({ ...t, isResolved: resolved, resolvedBy: resolved ? { login: by } : null }));

export const withIssueComment =
  (c: { id: string; author: Actor | null; body: string; at: string }) =>
  (s: PrState): PrState => ({
    ...s,
    timeline: [
      ...s.timeline,
      { __typename: 'IssueComment', id: c.id, databaseId: 0, createdAt: c.at, body: c.body, bodyHTML: '', url: '', viewerCanUpdate: false, author: c.author },
    ],
  });

export const withDraft =
  (d: ReviewDraft) =>
  (s: PrState): PrState => ({ ...s, drafts: [...s.drafts, d] });

const mapDraft = (s: PrState, id: ReviewDraft['id'], f: (d: ReviewDraft) => ReviewDraft): PrState => ({
  ...s,
  drafts: s.drafts.map((d) => (d.id === id ? f(d) : d)),
});

export const withDraftBody =
  (id: ReviewDraft['id'], body: string, at: string) =>
  (s: PrState): PrState =>
    mapDraft(s, id, (d) => ({ ...d, body, updated_at: at }));

export const withDraftState =
  (id: ReviewDraft['id'], state: DraftState, at: string) =>
  (s: PrState): PrState =>
    mapDraft(s, id, (d) => ({ ...d, state, updated_at: at }));

/** The viewer's pending review on GitHub gone, with its comments (threads left empty go too). */
export function withoutPendingReview(s: PrState): PrState {
  const threads = s.threads
    .map((t) => {
      const nodes = t.comments.nodes.filter((c) => c.pullRequestReview?.state !== 'PENDING');
      return nodes.length === t.comments.nodes.length ? t : { ...t, comments: { ...t.comments, totalCount: nodes.length, nodes } };
    })
    .filter((t) => t.comments.nodes.length > 0);
  return { ...s, threads, pr: s.pr && { ...s.pr, pending: { ...s.pr.pending, nodes: [] } } };
}

/**
 * The threads without the comments of the viewer's pending review on GitHub
 * that a draft mirrors (see logic/inbox `mirroredComment`): each is shown
 * once, as its draft. Only a comment still pending is hidden: one GitHub
 * shows as published (its review was submitted since the draft last
 * followed it) is part of the conversation, whatever a draft says. A
 * thread left empty goes too.
 */
export function withoutMirrored(threads: readonly ReviewThread[], drafts: readonly ReviewDraft[]): ReviewThread[] {
  const ids = new Set(drafts.flatMap((d) => mirroredComment(d) ?? []));
  if (!ids.size) return threads as ReviewThread[];
  return threads
    .map((t) => {
      const nodes = t.comments.nodes.filter((c) => !(ids.has(c.id) && c.pullRequestReview?.state === 'PENDING'));
      return nodes.length === t.comments.nodes.length ? t : { ...t, comments: { ...t.comments, totalCount: t.comments.totalCount - (t.comments.nodes.length - nodes.length), nodes } };
    })
    .filter((t) => t.comments.nodes.length > 0);
}

// ---------------------------------------------------------------- drafts in the code

/** The ID a draft's comment (and, for a new thread, the thread) has where review threads are shown. */
export const draftCommentId = (id: ReviewDraft['id']) => `draft:${id}`;

/** Whether a comment ID names one of changelist's drafts (see `draftCommentId`), known or not. */
export const isDraftCommentId = (commentId: string) => commentId.startsWith('draft:');

/** The draft a comment shown in a review thread is, or null for a comment GitHub has. */
export function draftIdOf(commentId: string, drafts: readonly ReviewDraft[]): ReviewDraft['id'] | null {
  if (!isDraftCommentId(commentId)) return null;
  return drafts.find((d) => draftCommentId(d.id) === commentId)?.id ?? null;
}

/** A draft as a comment of a review thread, in the review state `review` (`PENDING` while it is a draft). */
function asComment(d: ReviewDraft, author: Actor | null, review: string): ThreadComment {
  const commit = d.target.kind === 'reply' ? null : d.target.commit_sha;
  return { ...draftComment({ id: draftCommentId(d.id), author, body: d.body, at: d.created_at, commit }), pullRequestReview: { id: '', state: review } };
}

/** Who a draft is shown as written by: the viewer, or (an agent's draft) an agent working for them. */
export function draftActor(d: ReviewDraft, viewer: Actor | null): Actor | null {
  if (d.author !== 'agent') return viewer;
  return { __typename: 'Bot', login: viewer ? `agent for ${viewer.login}` : 'agent' };
}

/**
 * Threads with the drafts in them, shown where GitHub shows review threads:
 * a line or file draft as a thread of its own, a reply at the end of its
 * thread (a reply whose thread is not there is left out). Conversation
 * drafts are not threads. `viewer` writes the user's drafts; an agent's
 * show as `draftActor` says.
 */
export function withDraftThreads(threads: readonly ReviewThread[], drafts: readonly ReviewDraft[], viewer: Actor | null, review = 'PENDING'): ReviewThread[] {
  const replies = new Map<string, ThreadComment[]>();
  const added: ReviewThread[] = [];
  for (const d of drafts) {
    const t = d.target;
    const author = draftActor(d, viewer);
    if (t.kind === 'reply') replies.set(t.thread_id, [...(replies.get(t.thread_id) ?? []), asComment(d, author, review)]);
    else if (t.kind === 'line')
      added.push(
        draftThread(
          { id: draftCommentId(d.id), path: t.path, side: t.side, line: t.line, startLine: t.start_line, subjectType: 'LINE' },
          asComment(d, author, review),
        ),
      );
    else if (t.kind === 'file')
      added.push(draftThread({ id: draftCommentId(d.id), path: t.path, side: 'RIGHT', line: null, startLine: null, subjectType: 'FILE' }, asComment(d, author, review)));
  }
  if (!replies.size && !added.length) return threads as ReviewThread[];
  const out = threads.map((t) => {
    const more = replies.get(t.id);
    return more ? { ...t, comments: { ...t.comments, totalCount: t.comments.totalCount + more.length, nodes: [...t.comments.nodes, ...more] } } : t;
  });
  return [...out, ...added];
}

export const withAttention =
  (changes: AttentionChange[], written: Pick<ServerOverride, 'basis' | 'set_by' | 'set_at'>) =>
  (s: PrState): PrState => ({
    ...s,
    overrides: applyToOverrides(s.overrides, changes, written),
    attention: applyToMembers(s.attention, changes),
  });

/** The ID a conversation draft has in the conversation while a publish posts it. */
export const postingId = (id: ReviewDraft['id']) => `posting-${id}`;

/**
 * Publishing a plan (see `publishPlan`): its inline drafts leave the drafts
 * and show as the review's comments (in the state `event` gives them), its
 * conversation drafts as conversation comments.
 */
export const published =
  (plan: { verdict: ReviewEvent | null; inline: readonly ReviewDraft[]; conversation: readonly ReviewDraft[] }, author: Actor | null, at: string) =>
  (s: PrState): PrState => {
    const ids = [...plan.inline, ...plan.conversation].map((d) => d.id);
    let out: PrState = { ...s, drafts: s.drafts.filter((d) => !ids.includes(d.id)) };
    if (plan.verdict) out = { ...out, threads: withDraftThreads(out.threads, plan.inline, author, REVIEW_STATE[plan.verdict]) };
    for (const d of plan.conversation) out = withIssueComment({ id: postingId(d.id), author, body: d.body, at })(out);
    return out;
  };
